import { ApiClientError } from '../api/client';
import type { ReturnResult } from '../api/endpoints';
import { getAccessToken } from '../api/client';
import { addNotice } from './notices';
import { heldScanIds, type QueuedScan, type StationStore } from './store';

export interface ReturnScanApi {
  returnScan(
    eventId: string,
    code: string,
    undo: boolean,
    expectedRegistrationId?: string,
    guard?: ScanGuard,
  ): Promise<{ result: ReturnResult }>;
}

/** Schutz einer Sendung: Bricht ab (kein Senden, kein stilles Erneuern über das Cookie), wenn sie nicht mehr gelten darf. */
export interface ScanGuard {
  signal?: AbortSignal;
  /** Muss vor einem Wiederholen nach dem Erneuern der Sitzung noch stimmen (dieselbe Person). */
  stillValid?: () => boolean;
}

/** Ergebnis eines nachgesendeten Scans, das einen Hinweis verdient. */
export interface ReturnNotice {
  code: string;
  result: ReturnResult;
}

/** Ergebnisse, die nach dem Nachsenden keinen Hinweis brauchen: alles lief wie am Gerät angezeigt. */
const QUIET_RESULTS: ReadonlySet<ReturnResult> = new Set([
  'returned',
  'undone',
  // Schon vermerkt (z. B. doppelt gescannt, Antwort ging verloren): Der Artikel ist zurückgegeben, nichts ist zu tun
  'already_returned',
]);

export interface FlushOptions {
  /** Bricht den Lauf ab (Frist beim Abmelden, Verlassen der Seite): danach geht kein weiterer Scan hinaus. */
  signal?: AbortSignal;
}

/** Die Person, unter der gesendet wird: fest oder (besser) zum Zeitpunkt jedes Scans neu gelesen. */
export type PersonSource = string | null | (() => string | null);

export interface FlushSummary {
  sent: number;
  /** Nicht übertragbar (kein Netzwerkfehler, aber wiederholt abgelehnt): siehe `MAX_SCAN_ATTEMPTS`. */
  dropped: readonly string[];
  remaining: number;
  /**
   * Auffälligkeiten der nachgesendeten Scans (Plan 4.7.1): Der Server hat den Scan angenommen, aber nicht als „zurückgegeben“
   * (z. B. verkauft, nicht abgegeben, anderer Korb). Die Person hat den Artikel vielleicht schon herausgegeben: Sie soll es nach
   * dem Nachsenden erfahren. Die Hinweise stehen zusätzlich im Speicher der Station (`listNotices`), bis sie gelesen sind. Fehlt,
   * wenn es keine gibt.
   */
  notices?: readonly ReturnNotice[];
  /** Scans einer anderen Person: Sie bleiben in der Warteschlange und gehen nur unter deren Konto hinaus (fehlt, wenn keine). */
  held?: number;
}

/**
 * Ein Fehler, der nichts mit diesem einen Scan zu tun hat (kein Netz, keine Sitzung mehr, Basar gerade nicht in
 * „Rückgabe“, überlastet, Server-Fehler): Der Lauf bricht ab, ohne einen Versuch zu zählen, denn ein Wiederholen
 * hilft hier nichts und darf den Scan nicht verwerfen (Datenverlust). Nur ein Fehler zu genau diesem Scan (z. B. der
 * Etikettencode ist unbekannt) zählt als Versuch, bis `MAX_SCAN_ATTEMPTS` erreicht ist.
 */
export const isSystemFailure = (error: unknown): boolean => {
  if (!(error instanceof ApiClientError)) return true; // unerwarteter Fehler: nie als Fehler des Scans werten
  if (error.code === 'NETWORK') return true;
  return (
    error.status === 401 ||
    error.status === 403 ||
    error.status === 409 ||
    error.status === 429 ||
    error.status >= 500
  );
};

/**
 * Sendet einen Scan. Ein Scan mit Korb (O4) geht mit `expectedRegistrationId` hinaus. Ist der Korb-Modus inzwischen
 * ausgeschaltet (409 `BASKET_MODE_OFF`) oder lehnt ein älteres Image das Feld ab (400 `VALIDATION_ERROR`), gilt der Scan
 * als Einzelscan, statt nach 50 Versuchen verworfen zu werden.
 */
async function sendScan(
  api: ReturnScanApi,
  eventId: string,
  scan: QueuedScan,
  guard: ScanGuard,
): Promise<ReturnResult> {
  if (scan.expectedRegistrationId === undefined) {
    return (await api.returnScan(eventId, scan.code, scan.undo, undefined, guard)).result;
  }
  try {
    return (await api.returnScan(eventId, scan.code, scan.undo, scan.expectedRegistrationId, guard))
      .result;
  } catch (error) {
    // Korb-Modus inzwischen aus, oder ein älteres Image (Rollback) kennt das Feld nicht und lehnt es mit 400 ab
    if (
      error instanceof ApiClientError &&
      (error.code === 'BASKET_MODE_OFF' || error.code === 'VALIDATION_ERROR')
    ) {
      // Der zweite Aufruf ist ein neues Senden: Er gilt nur, wenn Person und Sitzung noch stimmen und der Lauf nicht abbrach
      if (guard.signal?.aborted === true || guard.stillValid?.() === false) {
        throw new ApiClientError(401, 'UNAUTHORIZED', 'Die Anmeldung gilt nicht mehr.');
      }
      return (await api.returnScan(eventId, scan.code, scan.undo, undefined, guard)).result;
    }
    throw error;
  }
}

/**
 * Überträgt die Warteschlange (HLP-06: Stationen funktionieren auch offline), siehe `isSystemFailure`. `person` ist die
 * angemeldete Person: Scans, die eine andere Person erfasst hat, bleiben liegen (kein Versuch, kein Fehlversuch, kein
 * Verwerfen), ebenso spätere Scans desselben Codes (Reihenfolge). Sie gehen hinaus, sobald sich diese Person wieder anmeldet
 * (Regel 10, Zuordnung der Person). Als Funktion übergeben wird die Person vor JEDEM Scan neu gelesen: Wechselt sie mitten im
 * Lauf (oder ist die Sitzung weg, oder bricht `options.signal` ab), geht kein weiterer Scan hinaus.
 */
export async function flushOutbox(
  store: StationStore,
  api: ReturnScanApi,
  eventId: string,
  person: PersonSource = null,
  options: FlushOptions = {},
): Promise<FlushSummary> {
  const read = typeof person === 'function' ? person : () => person;
  const live = typeof person === 'function';
  const start = read();
  const stillValid = (): boolean => read() === start && (!live || getAccessToken() !== null);
  const guard: ScanGuard = {
    ...(options.signal === undefined ? {} : { signal: options.signal }),
    stillValid,
  };
  const canGo = (): boolean => options.signal?.aborted !== true && stillValid();
  const dropped: string[] = [];
  const notices: ReturnNotice[] = [];
  let sent = 0;
  const scans = await store.all();
  const heldIds = heldScanIds(scans, start);
  const held = heldIds.size;
  for (const scan of scans) {
    if (heldIds.has(scan.id)) continue;
    if (!canGo()) break;
    try {
      const result = await sendScan(api, eventId, scan, guard);
      const noisy = !QUIET_RESULTS.has(result);
      // Der Hinweis wird zuerst gespeichert, dann der Scan entfernt: Er überlebt Neuladen und Abmelden (nie still)
      if (noisy) {
        await addNotice(store, {
          code: scan.code,
          result,
          userId: scan.userId ?? start,
          at: Date.now(),
        });
      }
      await store.markDone(scan.id);
      sent += 1;
      if (noisy) notices.push({ code: scan.code, result });
    } catch (error) {
      if (isSystemFailure(error)) break;
      if ((await store.markAttempt(scan.id)) === 'dropped') dropped.push(scan.code);
    }
  }
  return {
    sent,
    dropped,
    remaining: await store.pending(),
    ...(notices.length > 0 ? { notices } : {}),
    ...(held > 0 ? { held } : {}),
  };
}
